/**
 * OnlinePanel — Network monitoring dashboard (Group Admin & SuperAdmin).
 * Range selector + per-VLAN AreaChart + per-VLAN summary cards + Heatmap + Compare.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line,
} from 'recharts';
import {
  Wifi, RefreshCw, TrendingUp, TrendingDown, Minus, Activity, Calendar, BarChart3, AlertTriangle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RANGES = [
  { key: '3h', label: '🔴 3 saat', live: true },
  { key: '2d', label: '2 gün' },
  { key: '7d', label: '7 gün' },
  { key: '1m', label: '1 ay' },
  { key: '3m', label: '3 ay' },
  { key: '6m', label: '6 ay' },
  { key: '1y', label: '1 il' },
];

const AZ_MONTHS = ['yan', 'fev', 'mar', 'apr', 'may', 'iyn', 'iyl', 'avq', 'sen', 'okt', 'noy', 'dek'];
const AZ_WEEKDAYS = ['baz', 'b.e', 'ç.a', 'çər', 'c.a', 'cüm', 'şən'];

function fmtAzDay(d) {
  return `${d.getDate()} ${AZ_MONTHS[d.getMonth()]}`;
}

function fmtAzFull(ts) {
  const d = new Date(ts * 1000);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.getDate()} ${AZ_MONTHS[d.getMonth()]} ${d.getFullYear()}, ${hh}:${mm}`;
}

const LINE_COLORS = [
  '#8b5cf6', '#06b6d4', '#f59e0b', '#10b981', '#ef4444', '#ec4899',
  '#3b82f6', '#84cc16', '#a855f7', '#f97316', '#14b8a6', '#eab308',
];

function fmtNumber(n) {
  return new Intl.NumberFormat('az-AZ').format(n || 0);
}

function fmtAxisDate(ts, range) {
  const d = new Date(ts * 1000);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (range === '3h') {
    return `${hh}:${mm}`;
  }
  if (range === '2d') {
    return `${fmtAzDay(d)}, ${hh}:${mm}`;
  }
  if (range === '7d') {
    return `${AZ_WEEKDAYS[d.getDay()]} ${d.getDate()}`;
  }
  return fmtAzDay(d);
}

export default function OnlinePanel({ user }) {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const isSA = user?.role === 'superadmin' || user?.is_superadmin;

  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [range, setRange] = useState('3h');
  const [view, setView] = useState('chart'); // chart | heatmap | compare
  const [selectedIds, setSelectedIds] = useState(null); // null = bütün VLAN-lar seçili
  const [data, setData] = useState(null);
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setSelectedIds(null); }, [selectedGroupId]);

  const fetchGroups = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/network/groups`, { headers });
      setGroups(r.data || []);
      if (!selectedGroupId && r.data?.length) {
        setSelectedGroupId(r.data[0].id);
      }
    } catch (e) {
      // silent
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const selectedGroup = groups.find((g) => g.id === selectedGroupId);
  const allItemIds = useMemo(
    () => (selectedGroup?.zabbix_items || []).map((i) => String(i.zabbix_item_id)),
    [selectedGroup],
  );
  const effectiveIds = useMemo(
    () => (selectedIds === null ? allItemIds : allItemIds.filter((id) => selectedIds.has(id))),
    [selectedIds, allItemIds],
  );
  const isSubset = selectedIds !== null && effectiveIds.length < allItemIds.length;
  const noneSelected = selectedIds !== null && effectiveIds.length === 0;
  const itemIdsParam = isSubset && !noneSelected ? effectiveIds.join(',') : undefined;

  const toggleVlan = (id) => {
    setSelectedIds((prev) => {
      const base = prev === null ? new Set(allItemIds) : new Set(prev);
      if (base.has(id)) base.delete(id); else base.add(id);
      return base;
    });
  };

  const fetchMetrics = useCallback(async () => {
    if (!selectedGroupId) return;
    if (noneSelected) { setData({ items: [], total_series: [], current_total: 0, trend_pct: 0 }); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/network/metrics`, {
        params: { group_id: selectedGroupId, range, ...(itemIdsParam ? { item_ids: itemIdsParam } : {}) },
        headers,
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Məlumatları yükləmək alınmadı');
    } finally {
      setLoading(false);
    }
  }, [selectedGroupId, range, itemIdsParam, headers, noneSelected]);

  const fetchLive = useCallback(async () => {
    if (!selectedGroupId) return;
    try {
      const r = await axios.get(`${API}/network/live`, {
        params: { group_id: selectedGroupId },
        headers,
      });
      setLive(r.data);
    } catch {
      // silent
    }
  }, [selectedGroupId, headers]);

  useEffect(() => {
    if (view === 'chart') fetchMetrics();
  }, [fetchMetrics, view]);

  useEffect(() => {
    fetchLive();
    if (range === '3h' && view === 'chart') {
      // Live polling every 30s
      const id = setInterval(() => {
        fetchLive();
        fetchMetrics();
      }, 30000);
      return () => clearInterval(id);
    }
  }, [fetchLive, fetchMetrics, range, view]);

  // Build chart data from backend's properly-aligned total_series, augmented with per-VLAN values
  const chartData = useMemo(() => {
    if (!data) return [];
    // Primary source: total_series (already carry-forward summed)
    const totalMap = new Map((data.total_series || []).map((p) => [p.ts, p.value]));
    // Map of per-VLAN values for tooltip/per-line rendering
    const allTs = new Set(totalMap.keys());
    (data.items || []).forEach((it) => it.data.forEach((p) => allTs.add(p.ts)));
    const sortedTs = [...allTs].sort((a, b) => a - b);
    // Carry-forward each VLAN value too so per-VLAN lines are smooth
    const lastByVlan = {};
    return sortedTs.map((ts) => {
      const row = { ts };
      (data.items || []).forEach((it) => {
        const key = it.name || `vlan_${it.vlan}`;
        const found = it.data.find((p) => p.ts === ts);
        if (found) lastByVlan[key] = found.value;
        if (lastByVlan[key] !== undefined) row[key] = lastByVlan[key];
      });
      if (totalMap.has(ts)) row.Cəmi = totalMap.get(ts);
      return row;
    });
  }, [data]);

  // Trend % comes directly from backend (smoothed first 10% avg vs last 10% avg)
  const trend = data?.trend_pct ?? 0;

  // Live total — seçilmiş VLAN-lara görə filtrlənir
  const liveTotal = useMemo(() => {
    if (noneSelected) return 0;
    if (!isSubset) return live?.total || 0;
    return (live?.items || [])
      .filter((it) => effectiveIds.includes(String(it.item_id)))
      .reduce((s, it) => s + (it.value || 0), 0);
  }, [live, isSubset, effectiveIds, noneSelected]);

  // VLAN kartları — həmişə qrupun BÜTÜN VLAN-larını göstərir (seçim üçün)
  const cardItems = useMemo(() => {
    const liveMap = new Map((live?.items || []).map((it) => [String(it.item_id), it.value]));
    const statMap = new Map((data?.items || []).map((it) => [String(it.item_id), it]));
    return (selectedGroup?.zabbix_items || []).map((c) => {
      const id = String(c.zabbix_item_id);
      const st = statMap.get(id);
      return {
        item_id: id,
        vlan: c.vlan,
        name: c.display_name,
        current: liveMap.get(id) ?? st?.current ?? 0,
        min: st?.min,
        max: st?.max,
      };
    });
  }, [selectedGroup, live, data]);

  return (
    <div className="space-y-4" data-testid="online-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Wifi className="h-6 w-6 text-purple-600" />
            Online — Şəbəkə Monitorinqi
          </h2>
          <div className="text-xs text-zinc-500 mt-0.5">
            Zabbix-dən real-time abunəçi statistikası
          </div>
        </div>
        <div className="flex items-center gap-2">
          {groups.length > 1 && (
            <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
              <SelectTrigger className="w-48" data-testid="group-select">
                <SelectValue placeholder="Qrup seç..." />
              </SelectTrigger>
              <SelectContent>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name} ({g.zabbix_items?.length || 0} VLAN)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={() => { fetchMetrics(); fetchLive(); }} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {groups.length === 0 && (
        <div className="border-2 border-dashed rounded-lg p-12 text-center">
          <Wifi className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
          <div className="text-sm text-zinc-500">
            Heç bir qrup-VLAN əlaqəsi qurulmayıb.
          </div>
          {isSA && (
            <div className="text-xs text-zinc-400 mt-1">
              Sistem → Online Konfiqurasiyası bölməsindən qrup əlavə edin
            </div>
          )}
        </div>
      )}

      {selectedGroup && (
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard
              icon={Activity}
              label="Cari Online"
              value={fmtNumber(liveTotal)}
              color="bg-emerald-100 text-emerald-700"
              testid="stat-current"
            />
            <StatCard
              icon={BarChart3}
              label="Aktiv (son)"
              value={fmtNumber(data?.current_total || 0)}
              color="bg-blue-100 text-blue-700"
            />
            <StatCard
              icon={trend > 0 ? TrendingUp : trend < 0 ? TrendingDown : Minus}
              label={`Trend (${range})`}
              value={`${trend > 0 ? '+' : ''}${Number(trend).toFixed(1)}%`}
              color={trend > 0 ? 'bg-emerald-100 text-emerald-700'
                : trend < 0 ? 'bg-red-100 text-red-700'
                : 'bg-zinc-100 text-zinc-700'}
            />
            <StatCard
              icon={Wifi}
              label="VLAN sayı"
              value={isSubset ? `${effectiveIds.length}/${allItemIds.length}` : allItemIds.length}
              color="bg-purple-100 text-purple-700"
            />
          </div>

          {/* View selector */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-md border bg-white p-0.5">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setRange(r.key)}
                  className={`px-3 py-1.5 text-xs rounded transition ${
                    range === r.key ? 'bg-purple-600 text-white' : 'text-zinc-600 hover:bg-zinc-50'
                  }`}
                  data-testid={`range-${r.key}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <div className="inline-flex rounded-md border bg-white p-0.5">
              <button
                onClick={() => setView('chart')}
                className={`px-3 py-1.5 text-xs rounded ${view === 'chart' ? 'bg-purple-600 text-white' : 'text-zinc-600'}`}
                data-testid="view-chart"
              >
                <BarChart3 className="h-3.5 w-3.5 inline mr-1" /> Qrafik
              </button>
              <button
                onClick={() => setView('compare')}
                className={`px-3 py-1.5 text-xs rounded ${view === 'compare' ? 'bg-purple-600 text-white' : 'text-zinc-600'}`}
                data-testid="view-compare"
              >
                <Activity className="h-3.5 w-3.5 inline mr-1" /> Müqayisə
              </button>
              <button
                onClick={() => setView('heatmap')}
                className={`px-3 py-1.5 text-xs rounded ${view === 'heatmap' ? 'bg-purple-600 text-white' : 'text-zinc-600'}`}
                data-testid="view-heatmap"
              >
                <Calendar className="h-3.5 w-3.5 inline mr-1" /> Heatmap
              </button>
            </div>
            {range === '3h' && view === 'chart' && (
              <Badge className="bg-red-100 text-red-700 animate-pulse text-[10px]">🔴 LIVE</Badge>
            )}
            {loading && <span className="text-xs text-zinc-500">Yüklənir...</span>}
          </div>

          {view === 'chart' && (
            <div className="border rounded-lg bg-white p-4" data-testid="chart-container">
              {chartData.length === 0 && !loading ? (
                <div className="text-center text-sm text-zinc-500 py-16">
                  {noneSelected
                    ? 'Heç bir VLAN seçilməyib — aşağıdakı kartlardan seçim edin'
                    : 'Bu zaman aralığı üçün məlumat yoxdur'}
                </div>
              ) : (
              <ResponsiveContainer width="100%" height={380}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis
                    dataKey="ts"
                    tickFormatter={(ts) => fmtAxisDate(ts, range)}
                    tick={{ fontSize: 11 }}
                    minTickGap={50}
                  />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtNumber(v)} width={55} />
                  <Tooltip
                    labelFormatter={(ts) => fmtAzFull(ts)}
                    formatter={(value, name) => [fmtNumber(value), name]}
                    itemSorter={(item) => (item.dataKey === 'Cəmi' ? -Infinity : -(item.value || 0))}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {/* Cəmi area — primary always visible */}
                  <Area
                    type="monotone"
                    dataKey="Cəmi"
                    stroke="#8b5cf6"
                    strokeWidth={2.5}
                    fill="url(#totalGrad)"
                    isAnimationActive={false}
                  />
                  {/* Per-VLAN lines — həmişə göstərilir */}
                  {data?.items?.map((it, idx) => (
                    <Line
                      key={it.item_id}
                      type="monotone"
                      dataKey={it.name || `vlan_${it.vlan}`}
                      stroke={LINE_COLORS[idx % LINE_COLORS.length]}
                      strokeWidth={1.3}
                      dot={false}
                      isAnimationActive={false}
                      connectNulls
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
              )}
            </div>
          )}

          {view === 'compare' && (
            noneSelected ? (
              <div className="border rounded-lg bg-white p-4 text-center text-sm text-zinc-500 py-16" data-testid="compare-empty">
                Heç bir VLAN seçilməyib — aşağıdakı kartlardan seçim edin
              </div>
            ) : (
              <CompareView groupId={selectedGroupId} headers={headers} itemIds={itemIdsParam} />
            )
          )}

          {view === 'heatmap' && (
            noneSelected ? (
              <div className="border rounded-lg bg-white p-4 text-center text-sm text-zinc-500 py-16" data-testid="heatmap-empty">
                Heç bir VLAN seçilməyib — aşağıdakı kartlardan seçim edin
              </div>
            ) : (
              <HeatmapView groupId={selectedGroupId} headers={headers} itemIds={itemIdsParam} />
            )
          )}

          {/* Per-VLAN cards — klik ilə seçim */}
          {cardItems.length > 0 && view === 'chart' && (
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <div className="text-sm font-semibold">VLAN-lar üzrə</div>
                <div className="text-[10px] text-zinc-400">— karta klik edərək seçin, qrafik və hesabat seçilənlərə görə hesablanır</div>
                <div className="ml-auto flex items-center gap-3">
                  {effectiveIds.length < allItemIds.length && (
                    <button
                      onClick={() => setSelectedIds(null)}
                      className="text-xs text-purple-600 hover:underline font-medium"
                      data-testid="select-all-vlans-btn"
                    >
                      Hamısını seç ({allItemIds.length})
                    </button>
                  )}
                  {effectiveIds.length > 0 && (
                    <button
                      onClick={() => setSelectedIds(new Set())}
                      className="text-xs text-zinc-600 hover:text-red-600 hover:underline font-medium"
                      data-testid="deselect-all-vlans-btn"
                    >
                      Hamısının seçimini qaldır
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {cardItems.map((it, idx) => {
                  const isSelected = effectiveIds.includes(it.item_id);
                  return (
                    <button
                      key={it.item_id}
                      onClick={() => toggleVlan(it.item_id)}
                      className={`border rounded-md p-2.5 text-left transition ${
                        isSelected
                          ? 'bg-white border-purple-300 ring-1 ring-purple-200'
                          : 'bg-zinc-50 border-zinc-200 opacity-50 hover:opacity-80'
                      }`}
                      data-testid={`vlan-card-${it.vlan}`}
                      title={isSelected ? 'Seçimdən çıxar' : 'Seçimə əlavə et'}
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        <div
                          className={`h-2.5 w-2.5 rounded-full ${isSelected ? '' : 'opacity-40'}`}
                          style={{ backgroundColor: LINE_COLORS[idx % LINE_COLORS.length] }}
                        />
                        <div className="text-xs font-medium truncate flex-1" title={it.name}>{it.name}</div>
                        {isSelected && <span className="text-[10px] text-purple-600">✓</span>}
                      </div>
                      <div className="text-lg font-bold">{fmtNumber(it.current)}</div>
                      {it.min !== undefined && (
                        <div className="text-[10px] text-zinc-500">
                          min: {fmtNumber(it.min)} • max: {fmtNumber(it.max)}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {/* Recent anomalies */}
          {view === 'chart' && <RecentAnomalies groupId={selectedGroupId} headers={headers} />}
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="border rounded-lg p-3 bg-white" data-testid={testid}>
      <div className={`inline-flex items-center justify-center h-8 w-8 rounded-md ${color}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="text-2xl font-bold mt-1.5 text-zinc-800">{value}</div>
      <div className="text-xs text-zinc-500">{label}</div>
    </div>
  );
}

// ==================== Compare ====================
function CompareView({ groupId, headers, itemIds }) {
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!groupId) return;
    setLoading(true);
    axios
      .get(`${API}/network/compare`, {
        params: { group_id: groupId, period, ...(itemIds ? { item_ids: itemIds } : {}) },
        headers,
      })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Müqayisə məlumatları alınmadı'))
      .finally(() => setLoading(false));
  }, [groupId, period, itemIds, headers]);

  const merged = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    (data.current || []).forEach((p) => {
      map.set(p.ts, { ts: p.ts, Bu: p.value });
    });
    (data.previous || []).forEach((p) => {
      const row = map.get(p.ts) || { ts: p.ts };
      row.Öncəki = p.value;
      map.set(p.ts, row);
    });
    return [...map.values()].sort((a, b) => a.ts - b.ts);
  }, [data]);

  return (
    <div className="border rounded-lg bg-white p-4" data-testid="compare-container">
      <div className="flex items-center gap-2 mb-3">
        <Button
          variant={period === 'week' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setPeriod('week')}
          data-testid="compare-week"
        >
          Bu həftə vs ötən həftə
        </Button>
        <Button
          variant={period === 'month' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setPeriod('month')}
          data-testid="compare-month"
        >
          Bu ay vs ötən ay
        </Button>
        {loading && <span className="text-xs text-zinc-500">Yüklənir...</span>}
      </div>
      {merged.length === 0 && !loading ? (
        <div className="text-center text-sm text-zinc-500 py-16">
          Müqayisə üçün məlumat yoxdur
        </div>
      ) : (
      <ResponsiveContainer width="100%" height={360}>
        <AreaChart data={merged}>
          <defs>
            <linearGradient id="compareCurrGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.4} />
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="comparePrevGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#94a3b8" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#94a3b8" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
          <XAxis
            dataKey="ts"
            tickFormatter={(ts) => {
              const d = new Date(ts * 1000);
              return period === 'week'
                ? `${AZ_WEEKDAYS[d.getDay()]} ${d.getDate()}`
                : fmtAzDay(d);
            }}
            tick={{ fontSize: 11 }}
            minTickGap={50}
          />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtNumber(v)} width={55} />
          <Tooltip
            labelFormatter={(ts) => fmtAzFull(ts)}
            formatter={(v, n) => [fmtNumber(v), n === 'Bu' ? 'Bu period' : 'Öncəki period']}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Area type="monotone" dataKey="Öncəki" stroke="#94a3b8" fill="url(#comparePrevGrad)" strokeWidth={1.5} strokeDasharray="5 5" isAnimationActive={false} connectNulls />
          <Area type="monotone" dataKey="Bu" stroke="#8b5cf6" fill="url(#compareCurrGrad)" strokeWidth={2.5} isAnimationActive={false} connectNulls />
        </AreaChart>
      </ResponsiveContainer>
      )}
    </div>
  );
}

// ==================== Heatmap ====================
function HeatmapView({ groupId, headers, itemIds }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!groupId) return;
    setLoading(true);
    axios
      .get(`${API}/network/heatmap`, {
        params: { group_id: groupId, ...(itemIds ? { item_ids: itemIds } : {}) },
        headers,
      })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Heatmap alınmadı'))
      .finally(() => setLoading(false));
  }, [groupId, itemIds, headers]);

  const maxValue = data?.max_value || 1;
  const minValue = data?.min_value || 0;
  const range = Math.max(1, maxValue - minValue);

  return (
    <div className="border rounded-lg bg-white p-4" data-testid="heatmap-container">
      <div className="text-sm font-semibold mb-2 flex items-center gap-2">
        <Calendar className="h-4 w-4 text-purple-600" />
        Son 30 gün × 24 saat — abunəçi sıxlığı
        {loading && <span className="text-xs text-zinc-500 font-normal">Yüklənir...</span>}
      </div>
      <div className="text-[10px] text-zinc-500 mb-2">
        Hər kvadrat 1 saatlıq vaxt aralığını göstərir. Tünd = çox abunəçi
      </div>
      {!loading && minValue > 0 && (
        <div className="flex items-center gap-2 mb-3 text-[10px] text-zinc-600">
          <span>Az ({fmtNumber(minValue)})</span>
          <div className="flex h-3 w-40 rounded overflow-hidden border">
            {[0.15, 0.3, 0.45, 0.6, 0.75, 0.9, 1.0].map((op, i) => (
              <div key={i} className="flex-1" style={{ backgroundColor: `rgba(139, 92, 246, ${op})` }} />
            ))}
          </div>
          <span>Çox ({fmtNumber(maxValue)})</span>
        </div>
      )}
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          {/* Hour labels */}
          <div className="flex gap-[2px] pl-12 mb-1">
            {Array.from({ length: 24 }).map((_, h) => (
              <div key={h} className="w-[14px] text-[8px] text-zinc-400 text-center">
                {h % 3 === 0 ? h : ''}
              </div>
            ))}
          </div>
          {/* Grid */}
          {data?.grid?.map((row, dIdx) => {
            const dayTs = data.days[dIdx];
            const d = new Date(dayTs * 1000);
            return (
              <div key={dIdx} className="flex items-center gap-[2px] mb-[2px]">
                <div className="w-12 text-[10px] text-zinc-500 text-right pr-1">
                  {fmtAzDay(d)}
                </div>
                {row.map((v, hIdx) => {
                  // Linear normalization between min (non-zero) and max for clearer contrast
                  const intensity = v > 0 ? Math.max(0, Math.min(1, (v - minValue) / range)) : 0;
                  // 0 → very light gray; 1 → solid purple
                  const bg = v === 0
                    ? 'rgb(244, 244, 245)' // zinc-100 for "no data"
                    : `rgba(139, 92, 246, ${0.15 + intensity * 0.85})`;
                  return (
                    <div
                      key={hIdx}
                      className="w-[14px] h-[14px] rounded-[2px] cursor-pointer hover:ring-2 hover:ring-purple-400 transition"
                      style={{ backgroundColor: bg }}
                      title={`${fmtAzDay(d)} ${hIdx}:00 — ${fmtNumber(v)} abunəçi`}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ==================== Recent Anomalies Widget ====================
function RecentAnomalies({ groupId, headers }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/network/anomalies`, {
        params: { group_id: groupId, days: 7, limit: 20 },
        headers,
      });
      setItems(r.data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [groupId, headers]);

  useEffect(() => { load(); }, [load]);

  const ack = async (a) => {
    try {
      await axios.post(`${API}/network/anomalies/${a.id}/ack`, {}, { headers });
      toast.success('Təsdiqləndi');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Təsdiq alınmadı');
    }
  };

  if (!items.length && !loading) {
    return (
      <div className="border rounded-lg bg-white p-3 flex items-center gap-2 text-xs text-zinc-500" data-testid="anomalies-empty">
        <AlertTriangle className="h-4 w-4 text-emerald-600" />
        Son 7 gündə anomaliya aşkarlanmayıb
      </div>
    );
  }

  return (
    <div className="border rounded-lg bg-white" data-testid="recent-anomalies">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-3 hover:bg-zinc-50 transition"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <span className="font-semibold text-sm">Son anomaliyalar</span>
          <Badge className="bg-red-100 text-red-700 text-[10px]">{items.length}</Badge>
        </div>
        <span className="text-xs text-zinc-500">
          {items.filter((a) => !a.acknowledged).length} təsdiqlənməyib {expanded ? '▲' : '▼'}
        </span>
      </button>
      {expanded && (
        <div className="border-t divide-y">
          {items.map((a) => {
            const d = new Date(a.detected_at);
            const timeStr = `${d.getDate()} ${AZ_MONTHS[d.getMonth()]}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
            return (
              <div
                key={a.id}
                className={`p-3 flex items-center justify-between gap-3 ${a.acknowledged ? 'opacity-60' : ''}`}
                data-testid={`anomaly-${a.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{a.group_name}</span>
                    <Badge className="bg-red-100 text-red-700 text-[10px]">
                      -{fmtNumber(a.abs_drop)} abunə (-{a.drop_pct}%)
                    </Badge>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">
                      {a.trigger_reason === 'abs' ? 'mütləq' : a.trigger_reason === 'pct' ? 'faiz' : 'faiz+mütləq'}
                    </span>
                    {a.acknowledged && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">✓ təsdiqlənib</Badge>}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {timeStr} • {fmtNumber(a.before_value)} → {fmtNumber(a.after_value)} • {Math.round(a.window_seconds / 60)} dəq pəncərə
                  </div>
                </div>
                {!a.acknowledged && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => ack(a)}
                    data-testid={`ack-${a.id}`}
                    className="text-xs h-7"
                  >
                    Təsdiqlə
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
